import { IndicatorSeries, type Bounding, type IndicatorTemplate, type IndicatorDrawParams } from 'klinecharts';

// ── Window global written by useRSLines hook ─────────────────────────────────
export interface RSRenderConfig {
  id: string;
  color: string;
  visible: boolean;
  /** Display label, e.g. "ASI" or "SL20" */
  label: string;
  /** Map of unix_sec → rsScaled price level */
  rsMap: Map<number, number>;
  /** 1–99 rating; 0 = insufficient data */
  rsRating: number;
  /** Raw RS ratio data for new-high detection: unix_sec → rsRaw (stock/bench ratio) */
  rsRawMap: Map<number, number>;
  /** Pre-sorted entries of rsRawMap for efficient new-high detection in draw() */
  rawEntries: Array<[number, number]>;
  /** Extra downward pixel shift applied after anchor (from the slider, 0–200 px) */
  offsetPx: number;
  /** EMA of rsMap (same scale), or null when the EMA is disabled */
  emaMap: Map<number, number> | null;
  emaPeriod: number;
}

declare global {
  interface Window {
    __rs_lines?: RSRenderConfig[];
  }
}

// ── Drawing constants ─────────────────────────────────────────────────────────
const LINE_WIDTH          = 1.5;
const LABEL_FONT_SIZE     = 11;
const LABEL_PADDING_X     = 5;
const LABEL_PADDING_Y     = 3;
const LABEL_RADIUS        = 3;
const LABEL_OFFSET_X      = 8;   // px right of last bar
const NEW_HIGH_DOT_RADIUS = 3;
const NEW_HIGH_WINDOW     = 52;  // bars (≈ 1 year on weekly, ≈ 52 days on daily)
const EMA_LINE_WIDTH      = 1;
const EMA_FILL_ABOVE      = 'rgba(34, 197, 94, 0.18)';
const EMA_FILL_BELOW      = 'rgba(239, 83, 80, 0.18)';

type KBarBasic = { timestamp: number; open?: number; close?: number };

type RSDrawParams = IndicatorDrawParams<Record<string, never>> & {
  kLineDataList: KBarBasic[];
  ctx: CanvasRenderingContext2D;
  visibleRange: { from: number; to: number };
  xAxis: { convertToPixel: (i: number) => number };
  yAxis: { convertToPixel: (v: number) => number };
  bounding: Bounding;
};

type RSTooltipParams = {
  kLineDataList: KBarBasic[];
  crosshair: { dataIndex?: number };
};

// ── Indicator template ────────────────────────────────────────────────────────
const rsLine: IndicatorTemplate<Record<string, never>> = {
  name: 'RS_LINE',
  shortName: 'RS',
  series: IndicatorSeries.Normal,
  shouldOhlc: false,
  figures: [],

  // Pass-through calc: RS values come from window.__rs_lines, not klinecharts figures
  calc: (dataList) => dataList.map(() => ({})),

  // ── Hover tooltip ─────────────────────────────────────────────────────────
  createTooltipDataSource: (params: unknown) => {
    const p = params as RSTooltipParams;
    const configs = window.__rs_lines;
    if (!configs?.length) return { name: '', calcParamsText: '', icons: [], values: [] };

    const dataIndex = p.crosshair?.dataIndex ?? -1;
    const bar = dataIndex >= 0 ? p.kLineDataList[dataIndex] : null;
    if (!bar) return { name: '', calcParamsText: '', icons: [], values: [] };

    const unixSec = Math.floor(bar.timestamp / 1000);

    const values: Array<{ title: string; value: string }> = [];
    for (const c of configs) {
      if (!c.visible || !c.rsMap.has(unixSec)) continue;
      values.push({
        title: `RS ${c.label}: `,
        value: c.rsRating > 0 ? String(c.rsRating) : '—',
      });
      const ema = c.emaMap?.get(unixSec);
      if (ema != null) {
        values.push({
          title: `EMA${c.emaPeriod}: `,
          value: c.rsMap.get(unixSec)! >= ema ? 'above' : 'below',
        });
      }
    }

    return { name: 'RS', calcParamsText: '', icons: [], values };
  },

  // ── Canvas draw ───────────────────────────────────────────────────────────
  draw: (params: unknown): boolean => {
    try {
    const {
      ctx,
      kLineDataList,
      visibleRange,
      xAxis,
      yAxis,
    } = params as RSDrawParams;

    const configs = window.__rs_lines;
    if (!configs?.length) return false;

    const { from, to } = visibleRange;

    // ── Pre-compute per-line rescale factors ──────────────────────────────
    // Anchor = leftmostOpen - currentPrice * 10%
    // This places all RS lines ~10% below the open of the leftmost visible bar
    // so they don't overlap with the candles, regardless of the stock's price scale.

    // Find the latest visible close ("current price") for the 10% gap calculation.
    let currentPrice = 0;
    for (let i = to; i >= from; i--) {
      const bar = kLineDataList[i];
      if (bar?.close && bar.close > 0) { currentPrice = bar.close; break; }
    }

    const lineRescale = new Map<string, number>();
    for (const cfg of configs) {
      if (!cfg.visible || !cfg.rsMap.size) continue;
      for (let i = from; i <= to; i++) {
        const bar = kLineDataList[i];
        if (!bar) continue;
        const barOpen = bar.open ?? bar.close ?? 0;
        if (barOpen <= 0) continue;
        const unixSec = Math.floor(bar.timestamp / 1000);
        const rsVal = cfg.rsMap.get(unixSec);
        if (rsVal && rsVal > 0) {
          // base gap: 10% of current price below leftmost open
          // offsetPx is applied as a pixel shift after mapping to screen space
          const anchor = barOpen - currentPrice * 0.10;
          lineRescale.set(cfg.id, anchor / rsVal);
          break;
        }
      }
    }

    for (const cfg of configs) {
      if (!cfg.visible || !cfg.rsMap.size) continue;

      const rescale = lineRescale.get(cfg.id) ?? 1;

      // ── 1. Collect (x, y) points in visible range ─────────────────────
      const points: Array<{ x: number; y: number; emaY: number | null; unixSec: number; rsRaw: number }> = [];

      for (let i = from; i <= to; i++) {
        const bar = kLineDataList[i];
        if (!bar) continue;
        const unixSec = Math.floor(bar.timestamp / 1000);
        const rsScaled = cfg.rsMap.get(unixSec);
        if (rsScaled == null) continue;
        const rsRaw = cfg.rsRawMap.get(unixSec) ?? 0;
        const ema = cfg.emaMap?.get(unixSec);
        const emaY = ema != null ? yAxis.convertToPixel(ema * rescale) + cfg.offsetPx : null;
        points.push({ x: xAxis.convertToPixel(i), y: yAxis.convertToPixel(rsScaled * rescale) + cfg.offsetPx, emaY, unixSec, rsRaw });
      }

      if (points.length < 2) continue;

      ctx.save();

      // ── 1b. EMA fill + EMA line (drawn under the RS line) ─────────────
      if (cfg.emaMap) {
        const above = new Path2D();
        const below = new Path2D();
        for (let pi = 1; pi < points.length; pi++) {
          const p0 = points[pi - 1];
          const p1 = points[pi];
          if (p0.emaY == null || p1.emaY == null) continue;
          // Canvas y grows downward: d > 0 → RS above EMA
          const d0 = p0.emaY - p0.y;
          const d1 = p1.emaY - p1.y;
          if ((d0 >= 0) === (d1 >= 0)) {
            const path = d0 + d1 >= 0 ? above : below;
            path.moveTo(p0.x, p0.y);
            path.lineTo(p1.x, p1.y);
            path.lineTo(p1.x, p1.emaY);
            path.lineTo(p0.x, p0.emaY);
            path.closePath();
          } else {
            const t  = d0 / (d0 - d1);
            const xc = p0.x + t * (p1.x - p0.x);
            const yc = p0.y + t * (p1.y - p0.y);
            const first  = d0 > 0 ? above : below;
            const second = d0 > 0 ? below : above;
            first.moveTo(p0.x, p0.y);
            first.lineTo(xc, yc);
            first.lineTo(p0.x, p0.emaY);
            first.closePath();
            second.moveTo(xc, yc);
            second.lineTo(p1.x, p1.y);
            second.lineTo(p1.x, p1.emaY);
            second.closePath();
          }
        }
        ctx.fillStyle = EMA_FILL_ABOVE;
        ctx.fill(above);
        ctx.fillStyle = EMA_FILL_BELOW;
        ctx.fill(below);

        ctx.beginPath();
        ctx.setLineDash([4, 3]);
        ctx.strokeStyle = cfg.color;
        ctx.lineWidth   = EMA_LINE_WIDTH;
        ctx.globalAlpha = 0.7;
        let started = false;
        for (const p of points) {
          if (p.emaY == null) { started = false; continue; }
          if (!started) { ctx.moveTo(p.x, p.emaY); started = true; }
          else ctx.lineTo(p.x, p.emaY);
        }
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      // ── 2. Draw the RS line ───────────────────────────────────────────
      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.strokeStyle = cfg.color;
      ctx.lineWidth   = LINE_WIDTH;
      ctx.lineJoin    = 'round';

      for (let pi = 0; pi < points.length; pi++) {
        const { x, y } = points[pi];
        if (pi === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();

      // ── 3. Draw RS rating label at right edge ────────────────────────
      const last = points[points.length - 1];
      const ratingText = cfg.rsRating > 0 ? `RS ${cfg.rsRating}` : 'RS —';

      ctx.font         = `bold ${LABEL_FONT_SIZE}px sans-serif`;
      const textWidth  = ctx.measureText(ratingText).width;
      const boxW       = textWidth + LABEL_PADDING_X * 2;
      const boxH       = LABEL_FONT_SIZE + LABEL_PADDING_Y * 2;
      const boxX       = last.x + LABEL_OFFSET_X;
      const boxY       = last.y - boxH / 2;

      // Rounded rectangle background
      ctx.beginPath();
      ctx.moveTo(boxX + LABEL_RADIUS, boxY);
      ctx.lineTo(boxX + boxW - LABEL_RADIUS, boxY);
      ctx.quadraticCurveTo(boxX + boxW, boxY, boxX + boxW, boxY + LABEL_RADIUS);
      ctx.lineTo(boxX + boxW, boxY + boxH - LABEL_RADIUS);
      ctx.quadraticCurveTo(boxX + boxW, boxY + boxH, boxX + boxW - LABEL_RADIUS, boxY + boxH);
      ctx.lineTo(boxX + LABEL_RADIUS, boxY + boxH);
      ctx.quadraticCurveTo(boxX, boxY + boxH, boxX, boxY + boxH - LABEL_RADIUS);
      ctx.lineTo(boxX, boxY + LABEL_RADIUS);
      ctx.quadraticCurveTo(boxX, boxY, boxX + LABEL_RADIUS, boxY);
      ctx.closePath();

      ctx.globalAlpha = 0.18;
      ctx.fillStyle   = cfg.color;
      ctx.fill();
      ctx.globalAlpha = 1;

      ctx.strokeStyle = cfg.color;
      ctx.lineWidth   = 1;
      ctx.stroke();

      ctx.fillStyle   = cfg.color;
      ctx.textBaseline = 'middle';
      ctx.textAlign    = 'left';
      ctx.fillText(ratingText, boxX + LABEL_PADDING_X, boxY + boxH / 2);

      // ── 4. RS new-high dots ──────────────────────────────────────────
      // Use pre-sorted rawEntries from RSRenderConfig (computed once, not per-frame)
      const rawEntries = cfg.rawEntries;

      // For each visible point, determine if it's a new 52-bar high in rsRaw
      for (const pt of points) {
        if (!pt.rsRaw) continue;

        // Find index in rawEntries for this bar
        let ptIdx = -1;
        for (let ri = 0; ri < rawEntries.length; ri++) {
          if (rawEntries[ri][0] === pt.unixSec) { ptIdx = ri; break; }
        }
        if (ptIdx < 0) continue;

        // Compute max over the prior NEW_HIGH_WINDOW entries (not including current)
        const windowStart = Math.max(0, ptIdx - NEW_HIGH_WINDOW);
        let priorMax = 0;
        for (let ri = windowStart; ri < ptIdx; ri++) {
          if (rawEntries[ri][1] > priorMax) priorMax = rawEntries[ri][1];
        }

        // It's a new high if current rsRaw exceeds all prior NEW_HIGH_WINDOW bars
        if (priorMax > 0 && pt.rsRaw > priorMax) {
          ctx.beginPath();
          ctx.arc(pt.x, pt.y - NEW_HIGH_DOT_RADIUS - 2, NEW_HIGH_DOT_RADIUS, 0, Math.PI * 2);
          ctx.fillStyle = cfg.color;
          ctx.fill();
        }
      }

      ctx.restore();
    }

    return false;
    } catch (e) {
      console.error('[RSLine] draw error:', e);
      return false;
    }
  },
};

export default rsLine;
